// Colores que Tiendanube dibuja como círculo en la tienda.
//
// ADR-2026-09-06-lanzamientos-colores-de-tiendanube.
//
// CÓMO FUNCIONA DE VERDAD (verificado en la documentación, no supuesto):
//
//   · La API **no acepta un hex**. `attributes` es una lista de textos
//     localizados (`[{es: "Color"}]`) y no admite tipo ni valor de color, y no
//     hay endpoint para setear el `custom_data` que la plantilla usa como
//     `background` del círculo.
//   · El círculo aparece cuando el atributo se llama exactamente **"Color"**
//     (o "Cor" en portugués) y el valor coincide **exacto** con un nombre de la
//     lista de colores de Tiendanube, respetando acentos, espacios y mayúsculas.
//   · Si el nombre no está en la lista, la tienda lo muestra como texto plano.
//   · Además el comerciante tiene que tildar "Mostrar como botones" en el diseño
//     de su tienda. Eso no se puede hacer por API y no está en todos los diseños
//     (Base, Cali, Uyuni, Lima y Rio no lo tienen).
//
// Por eso acá no hay un campo de hex: hay una LISTA CERRADA de nombres. El hex
// que acompaña a cada uno es **nuestro**, aproximado, y sirve solo para dibujar
// la muestra dentro de Atlas mientras se elige. Nunca viaja a Tiendanube.

export interface ColorTiendanube {
  /** El nombre EXACTO que espera Tiendanube. Es lo único que viaja. */
  nombre: string;
  /** Aproximación para la muestra dentro de Atlas. No se publica. */
  hex: string;
}

/**
 * La lista de la ayuda de Tiendanube, agrupada como la publican ellos. El orden
 * dentro de cada familia es el de la fuente.
 */
export const COLORES_TIENDANUBE: ReadonlyArray<{
  familia: string;
  colores: ReadonlyArray<ColorTiendanube>;
}> = [
  {
    familia: "Grises y neutros",
    colores: [
      { nombre: "Negro", hex: "#000000" },
      { nombre: "Preto fosco", hex: "#1A1A1A" },
      { nombre: "Grafito", hex: "#383838" },
      { nombre: "Gris oscuro", hex: "#4D4D4D" },
      { nombre: "Gris plomo", hex: "#5A5A5A" },
      { nombre: "Gris topo", hex: "#7C7268" },
      { nombre: "Gris", hex: "#808080" },
      { nombre: "Cemento", hex: "#8C8C8C" },
      { nombre: "Acero", hex: "#9AA3AB" },
      { nombre: "Plata", hex: "#C0C0C0" },
      { nombre: "Platino", hex: "#CFCFCF" },
      { nombre: "Gris claro", hex: "#D3D3D3" },
      { nombre: "Ceniza", hex: "#D9D6D0" },
      { nombre: "Tiza", hex: "#EDEAE3" },
      { nombre: "Hueso", hex: "#F1E9DC" },
      { nombre: "Blanco", hex: "#FFFFFF" },
    ],
  },
  {
    familia: "Azules",
    colores: [
      { nombre: "Azul noche", hex: "#0D1B3E" },
      { nombre: "Azul marino", hex: "#122B54" },
      { nombre: "Azul oscuro", hex: "#16337A" },
      { nombre: "Petróleo", hex: "#1B4A5A" },
      { nombre: "Azul petróleo", hex: "#1F5566" },
      { nombre: "Azul", hex: "#1A4FD6" },
      { nombre: "Real", hex: "#2455C7" },
      { nombre: "Azul francia", hex: "#2E6BD1" },
      { nombre: "Jean", hex: "#4A6FA5" },
      { nombre: "Azul acero", hex: "#4682B4" },
      { nombre: "Azulino", hex: "#5C86D6" },
      { nombre: "Turquesa", hex: "#2FC1C9" },
      { nombre: "Cyan", hex: "#22D3EE" },
      { nombre: "Celeste", hex: "#7FC3E8" },
      { nombre: "Celeste pastel", hex: "#BFDFF2" },
    ],
  },
  {
    familia: "Verdes",
    colores: [
      { nombre: "Verde inglés", hex: "#14372B" },
      { nombre: "Verde oscuro", hex: "#1B5E20" },
      { nombre: "Verde militar", hex: "#4B5320" },
      { nombre: "Verde oliva", hex: "#6B7A2E" },
      { nombre: "Verde musgo", hex: "#697A45" },
      { nombre: "Verde seco", hex: "#8A8C5C" },
      { nombre: "Esmeralda", hex: "#0FA36B" },
      { nombre: "Verde", hex: "#2E9E44" },
      { nombre: "Verde manzana", hex: "#5FBF3F" },
      { nombre: "Verde lima", hex: "#8CCB2E" },
      { nombre: "Verde neon", hex: "#39FF14" },
      { nombre: "Verde claro", hex: "#8FD98F" },
      { nombre: "Pera", hex: "#C4D64B" },
      { nombre: "Verde agua", hex: "#A8E6CF" },
      { nombre: "Verde pastel", hex: "#C7E9C0" },
    ],
  },
  {
    familia: "Marrones y tierras",
    colores: [
      { nombre: "Chocolate", hex: "#3B2216" },
      { nombre: "Café", hex: "#4B3225" },
      { nombre: "Marrón oscuro", hex: "#4E342E" },
      { nombre: "Sepia", hex: "#5C4433" },
      { nombre: "Marrón", hex: "#6B4423" },
      { nombre: "Suela", hex: "#7A5230" },
      { nombre: "Pardo", hex: "#7C5C43" },
      { nombre: "Siena", hex: "#8A4B2A" },
      { nombre: "Madera", hex: "#8B5A2B" },
      { nombre: "Marrón claro", hex: "#9C6B4A" },
      { nombre: "Avellana", hex: "#A8763E" },
      { nombre: "Caramelo", hex: "#B26B2E" },
      { nombre: "Habano", hex: "#B08654" },
      { nombre: "Visón", hex: "#B3A394" },
      { nombre: "Peltre", hex: "#9C9188" },
      { nombre: "Camel", hex: "#C19A6B" },
      { nombre: "Caqui", hex: "#C3B091" },
      { nombre: "Coco", hex: "#C9A98A" },
      { nombre: "Nude", hex: "#D9B7A0" },
      { nombre: "Arena", hex: "#DCC9A6" },
      { nombre: "Beige", hex: "#E4D5B7" },
      { nombre: "Natural", hex: "#E6DBC7" },
      { nombre: "Crema", hex: "#F2E7D0" },
      { nombre: "Marfil", hex: "#F7F0DC" },
      { nombre: "Perla", hex: "#F5EFE6" },
      { nombre: "Champagne", hex: "#EFDEC2" },
    ],
  },
  {
    familia: "Amarillos y dorados",
    colores: [
      { nombre: "Ocre", hex: "#B8860B" },
      { nombre: "Mostaza", hex: "#D4A017" },
      { nombre: "Dorado", hex: "#D4AF37" },
      { nombre: "Ámbar", hex: "#E8A317" },
      { nombre: "Miel", hex: "#E0A93B" },
      { nombre: "Maíz", hex: "#F3D250" },
      { nombre: "Amarillo", hex: "#FFD400" },
      { nombre: "Amarillo neon", hex: "#F2FF00" },
      { nombre: "Limón", hex: "#F4EE5A" },
      { nombre: "Manteca", hex: "#F7EFC0" },
      { nombre: "Amarillo pastel", hex: "#FBF3B0" },
      { nombre: "Cobre", hex: "#B87333" },
      { nombre: "Bronce", hex: "#A9772E" },
    ],
  },
  {
    familia: "Rosas y violetas",
    colores: [
      { nombre: "Púrpura", hex: "#5B2A86" },
      { nombre: "Morado", hex: "#6A2C91" },
      { nombre: "Indigo", hex: "#4B0082" },
      { nombre: "Obispo", hex: "#6B4C7A" },
      { nombre: "Violeta", hex: "#7C4DBE" },
      { nombre: "Ciruela", hex: "#7A3B62" },
      { nombre: "Borgoña", hex: "#6D1F33" },
      { nombre: "Borravino", hex: "#7B2233" },
      { nombre: "Frambuesa", hex: "#C2185B" },
      { nombre: "Fucsia", hex: "#E01E84" },
      { nombre: "Fucsia fluo", hex: "#FF00A0" },
      { nombre: "Chicle", hex: "#F25FA0" },
      { nombre: "Rosa fluo", hex: "#FF4FCB" },
      { nombre: "Rosa oscuro", hex: "#C2557A" },
      { nombre: "Malva", hex: "#B784A7" },
      { nombre: "Rosa viejo", hex: "#C48793" },
      { nombre: "Rosa", hex: "#F19CB5" },
      { nombre: "Sandía", hex: "#F2607A" },
      { nombre: "Flamenco", hex: "#F4899C" },
      { nombre: "Rosa claro", hex: "#F7BFCC" },
      { nombre: "Rosa bebé", hex: "#F9D3DC" },
      { nombre: "Rosa pastel", hex: "#FBDDE4" },
      { nombre: "Lila", hex: "#C9A8E0" },
      { nombre: "Lavanda", hex: "#D8CCEE" },
      { nombre: "Rose gold", hex: "#E0A899" },
    ],
  },
  {
    familia: "Rojos y naranjas",
    colores: [
      { nombre: "Bordó", hex: "#5C1A1B" },
      { nombre: "Vino", hex: "#6B1F2E" },
      { nombre: "Granate", hex: "#7B1E27" },
      { nombre: "Caoba", hex: "#8B2E20" },
      { nombre: "Cereza", hex: "#B32134" },
      { nombre: "Rojo", hex: "#DC2626" },
      { nombre: "Colorado", hex: "#D0342C" },
      { nombre: "Tomate", hex: "#E14B34" },
      { nombre: "Frutilla", hex: "#E2445C" },
      { nombre: "Óxido", hex: "#A85432" },
      { nombre: "Terracota", hex: "#C56A4A" },
      { nombre: "Naranja", hex: "#F26B21" },
      { nombre: "Naranja fluo", hex: "#FF6A00" },
      { nombre: "Mandarina", hex: "#F58634" },
      { nombre: "Coral", hex: "#F58573" },
      { nombre: "Salmón", hex: "#F79E86" },
      { nombre: "Durazno", hex: "#F9BE9A" },
      { nombre: "Piel", hex: "#F0C9A8" },
    ],
  },
];

/** Todos los colores, sin agrupar. */
export const COLORES_PLANOS: ReadonlyArray<ColorTiendanube> = COLORES_TIENDANUBE.flatMap(
  (g) => g.colores,
);

/** El atributo tiene que llamarse así para que la tienda dibuje el círculo. */
const NOMBRES_DE_ATRIBUTO_COLOR = ["color", "cor"];

function normalizar(v: string): string {
  return v
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

/**
 * `true` si este atributo es el que Tiendanube trata como color. La plantilla
 * compara contra "Color" y "Cor"; acá se normaliza para que "color" en minúscula
 * también active la ayuda dentro de Atlas (en la tienda igual hay que escribirlo
 * como corresponde, y de eso avisa la UI).
 */
export function esAtributoColor(nombre: string | null | undefined): boolean {
  return NOMBRES_DE_ATRIBUTO_COLOR.includes(normalizar(nombre ?? ""));
}

/**
 * El color de la lista que coincide con lo escrito, o `null`.
 *
 * El match para ENCONTRARLO es tolerante (sin acentos ni mayúsculas) porque
 * sirve para dibujar la muestra y para sugerir la corrección. Lo que se publica
 * es siempre `color.nombre`, con la grafía exacta que Tiendanube exige.
 */
export function colorDeTiendanube(valor: string | null | undefined): ColorTiendanube | null {
  const v = normalizar(valor ?? "");
  if (v === "") return null;
  return COLORES_PLANOS.find((c) => normalizar(c.nombre) === v) ?? null;
}

/**
 * `true` si el valor se va a ver como círculo en la tienda. Exige la grafía
 * EXACTA: "rojo" no alcanza, tiene que ser "Rojo".
 */
export function seVeComoCirculo(valor: string | null | undefined): boolean {
  const v = (valor ?? "").trim();
  if (v === "") return false;
  return COLORES_PLANOS.some((c) => c.nombre === v);
}

/** Colores de la lista que empiezan o contienen lo tipeado, para el buscador. */
export function buscarColores(texto: string, limite = 60): ColorTiendanube[] {
  const t = normalizar(texto);
  if (t === "") return COLORES_PLANOS.slice(0, limite);
  const empiezan: ColorTiendanube[] = [];
  const contienen: ColorTiendanube[] = [];
  for (const c of COLORES_PLANOS) {
    const n = normalizar(c.nombre);
    if (n.startsWith(t)) empiezan.push(c);
    else if (n.includes(t)) contienen.push(c);
  }
  return [...empiezan, ...contienen].slice(0, limite);
}
